import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Globe, Inbox, Loader2, Plus, Share2, Trash2, Users } from 'lucide-react';

interface Item {
  itemId: string;
  sessionName: string;
  title: string;
  kind: string;
  contentType: string;
  sizeBytes: number;
  publishedAt: string;
  updated: boolean;
  state: 'available' | 'unavailable';
  audience: Audience;
}

type Audience = 'session' | 'hub';
const AUDIENCE_LABEL: Record<Audience, string> = { session: "This session's attendees and the sponsor", hub: 'Everyone on the hub' };

interface Available {
  sessionId: string;
  sessionName: string;
  taskId: string;
  outputIndex: number;
  title: string;
  kind: string;
  contentType: string;
  sizeBytes: number;
  completedAt: string | null;
  sharedItemId: string | null;
}

interface Props {
  /** Calls the setup API: path relative to the hub, e.g. '/items' */
  call: <T>(path: string, method?: string, data?: unknown) => Promise<T>;
  /** Called after a change, so the activity list can refresh */
  onChanged?: () => void;
}

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const size = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const typeLabel = (ct: string) => (ct === 'text/html' ? 'Report' : ct === 'application/pdf' ? 'PDF' : ct.startsWith('video/') ? 'Film' : ct.startsWith('image/') ? 'Image' : 'File');

const primaryBtn = 'flex items-center justify-center gap-2 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60';
const secondaryBtn = 'flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-60';

export function LibraryEditor({ call, onChanged }: Props) {
  const [items, setItems] = useState<Item[] | null>(null);
  const [available, setAvailable] = useState<Available[] | null>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audience, setAudience] = useState<Audience>('session');

  const loadItems = useCallback(async () => {
    try {
      setItems((await call<{ items: Item[] }>('/items')).items);
    } catch (e: any) {
      setError(e.message);
    }
  }, [call]);

  const loadAvailable = useCallback(async () => {
    try {
      setAvailable((await call<{ outputs: Available[] }>('/shareable-outputs')).outputs);
    } catch (e: any) {
      setError(e.message);
    }
  }, [call]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { if (picking) loadAvailable(); }, [picking, loadAvailable]);

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      onChanged?.();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }

  const key = (d: Available) => `${d.sessionId}|${d.taskId}|${d.outputIndex}`;

  const share = (d: Available) => run(key(d), async () => {
    await call('/items', 'POST', { sessionId: d.sessionId, taskId: d.taskId, outputIndex: d.outputIndex, audience });
    await Promise.all([loadItems(), loadAvailable()]);
  });

  const toggleAudience = (i: Item) => run(i.itemId, async () => {
    await call(`/items/${encodeURIComponent(i.itemId)}`, 'PATCH', { audience: i.audience === 'hub' ? 'session' : 'hub' });
    await loadItems();
  });

  const unshare = (itemId: string) => run(itemId, async () => {
    await call(`/items/${encodeURIComponent(itemId)}`, 'DELETE');
    await loadItems();
    if (picking) await loadAvailable();
  });

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3">
        <h4 className="text-[11px] font-bold text-primary tracking-widest uppercase flex items-center gap-1.5"><Share2 size={14} /> Shared with visitors</h4>
        <button type="button" className={secondaryBtn} onClick={() => setPicking(!picking)}>
          {picking ? 'Done' : <><Plus size={16} /> Share a deliverable</>}
        </button>
      </div>
      {error && (
        <div className="mx-5 mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      )}

      {picking && (
        <div className="border-t border-[#e2e8f0] bg-slate-50/50">
          <p className="px-5 pt-3 text-xs text-slate-500">HTML reports, PDFs, films and images from this project's session tasks. You can also share from a task's output with Publish to Session Hub.</p>
          <div className="px-5 pt-3 flex flex-wrap items-center gap-2">
            <label htmlFor="library-audience" className="text-xs font-semibold text-slate-500">Share with</label>
            <select id="library-audience" value={audience} onChange={(e) => setAudience(e.target.value as Audience)} className="border border-[#e2e8f0] rounded-lg h-8 px-2 text-xs text-navy bg-white">
              {(Object.keys(AUDIENCE_LABEL) as Audience[]).map((a) => <option key={a} value={a}>{AUDIENCE_LABEL[a]}</option>)}
            </select>
          </div>
          {available === null ? (
            <div className="flex justify-center py-6 text-[#64748b]"><Loader2 size={18} className="animate-spin" /></div>
          ) : available.length === 0 ? (
            <p className="px-5 py-4 text-sm text-slate-400">No report, PDF, film or image outputs in this project's sessions yet.</p>
          ) : (
            <ul className="divide-y divide-[#e2e8f0] mt-2">
              {available.map((d) => (
                <li key={key(d)} className="px-5 py-3 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[12rem]">
                    <p className="text-sm font-semibold text-navy">{d.title}</p>
                    <p className="text-xs text-slate-400">{d.sessionName} · {typeLabel(d.contentType)}{d.sizeBytes ? ` · ${size(d.sizeBytes)}` : ''}</p>
                  </div>
                  {d.sharedItemId ? (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">Shared</span>
                  ) : (
                    <button type="button" className={primaryBtn} disabled={busy === key(d)} onClick={() => share(d)}>
                      {busy === key(d) ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />} Share
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="border-t border-[#e2e8f0]">
        {items === null ? (
          <div className="flex justify-center py-8 text-[#64748b]"><Loader2 size={20} className="animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
            <Inbox size={22} className="text-slate-300" /> Nothing shared yet.
          </div>
        ) : (
          <ul className="divide-y divide-[#e2e8f0]">
            {items.map((i) => (
              <li key={i.itemId} className="px-5 py-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[12rem]">
                  <p className={`text-sm font-semibold ${i.state === 'available' ? 'text-navy' : 'text-slate-400 line-through'}`}>{i.title}</p>
                  <p className="text-xs text-slate-400">
                    {i.sessionName}{i.state === 'available' ? ` · ${typeLabel(i.contentType)}${i.sizeBytes ? ` · ${size(i.sizeBytes)}` : ''}` : ''} · shared {fmtDate(i.publishedAt)}
                  </p>
                </div>
                {i.updated && i.state === 'available' && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20" title="A newer version was published, so visitors see that one">Updated to latest</span>
                )}
                {i.state === 'available' && (
                  <button
                    type="button" disabled={busy === i.itemId} onClick={() => toggleAudience(i)}
                    title={i.audience === 'hub' ? 'Everyone on the hub can see this. Click to limit it to the session\'s attendees and the sponsor.' : 'Only the session\'s attendees and the sponsor can see this. Click to share with everyone on the hub.'}
                    className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200 hover:border-primary/40 flex items-center gap-1"
                  >
                    {i.audience === 'hub' ? <><Globe size={12} /> Everyone</> : <><Users size={12} /> Session attendees</>}
                  </button>
                )}
                {i.state === 'unavailable' && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200" title="Deleted, rejected or its session's content was removed">Not available</span>
                )}
                <button type="button" className="text-slate-400 hover:text-red-600" title="Stop sharing" disabled={busy === i.itemId} onClick={() => unshare(i.itemId)}>
                  {busy === i.itemId ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
